import { PaymentMethodType } from './payment-types.types';
import {
  ChangedAttributes,
  EntityTypes,
  Timestamps,
  Versioned,
} from './shared.types';

export enum DebtOriginType {
  RECEIPT = 'RECEIPT',
  LOAN = 'LOAN',
  TRANSFER = 'TRANSFER',
}

export interface DebtorGroup {
  debtor_id: string;
  debtor_type: EntityTypes;
  debtor_name: string;
  debts: Debt[];
  total_outstanding_amount: number;
  total_paid_amount: number;
  total_amount: number;
}

/**
 * Enum representing the current status of a debt.
 */
export enum DebtStatus {
  ACTIVE = 'ACTIVE',
  CLOSED = 'CLOSED',
  OVERDUE = 'OVERDUE',
  DEFAULTED = 'DEFAULTED',
  CANCELLED = 'CANCELLED',
  TRANSFERRED = 'TRANSFERRED',
  REVERSED = 'REVERSED',
}

export interface DebtTransfer extends Timestamps {
  id: string;
  from_debt_id: string;
  to_debt_id: string;
  store_id: string;
  merchant_id: string;
  note: string;
  creator_id: string;
  creator_name: string;
  creator_type: EntityTypes;
  amount: number;
}

export interface Debt extends Timestamps {
  id: string;
  creator_type: EntityTypes; // Employees(user) or Receipts
  creator_id: string;
  creator_name: string;
  debtor_id: string; // Employee or Customer
  debtor_name: string;
  debtor_type: EntityTypes;
  store_id: string;
  merchant_id: string;
  transfers: DebtTransfer[];
  origin_type: DebtOriginType;
  principal_amount: number;
  outstanding_balance: number;
  interest_rate: number; // e.g., 5.5 for 5.5%
  status: DebtStatus;
  next_due_date: string | null | undefined; // ISO 8601 date string, null if auto-calculated
  closed_date: string | null | undefined;
  payment_plans: PaymentPlan[]; // The latest payment plan is the active one
  last_payment_date?: string;
  is_due_date_auto_calculated: boolean;
  reason: string;
  receipt_id?: string;
  version: string; // Version of the debt
  /**
   * The row's numeric version for the versioned-write helper (`version`
   * above is the change-log id). Optional for backwards compatibility;
   * missing = 0.
   */
  row_version?: number | null;
  /** Who made the latest change. Optional for backwards compatibility. */
  updated_by_id?: string | null;
  updated_by_name?: string | null;
  cancellation_reason?: string;
  cancelled_at?: string;
  reversed_at?: string;
  reversed_by_user_id?: string;
  reversal_reason?: string;
  shift_id?: string; // TODO: Make Optional // Shift during which the debt was created
}

export enum DebtEvent {
  DEBT_STATUS_UPDATED = 'debt_status_updated',
  DEBT_CREATED = 'debt_created',
  DEBT_TRANSFERRED = 'debt_transferred',
  DEBT_INTEREST_UPDATED = 'debt_interest_updated',
  DEBT_DUE_DATE_UPDATED = 'debt_due_date_updated',
  DEBT_CLOSING_DATE_UPDATED = 'debt_closing_date_updated',
  DEBT_PLAN_AMOUNT_UPDATED = 'debt_payment_plan_amount_updated',
  DEBT_PLAN_FREQUENCY_UPDATED = 'debt_payment_plan_frequency_updated',
  DEBT_PLAN_NOTE_UPDATED = 'debt_payment_plan_note_updated',
  DEBT_PAYMENT_TYPE_UPDATED = 'debt_payment_type_updated',
  DEBT_CREATOR_NAME_UPDATED = 'debt_creator_name_updated',
  DEBTOR_NAME_UPDATED = 'debtor_name_updated',
  DEBT_PRINCIPAL_AMOUNT_UPDATED = 'debt_principal_amount_updated',
  DEBT_OUTSTANDING_BALANCE_UPDATED = 'debt_outstanding_balance_updated',
  DEBT_PAYMENT_OUTSTANDING_BALANCE_UPDATED = 'debt_payment_outstanding_balance_updated',
  DEBT_DELETED = 'debt_deleted',
  DEBT_PAYMENT_UPDATED = 'debt_payment_updated',
  AUTO_CALCULATED_DUE_DATE_UPDATED = 'auto_calculated_due_date_updated',
  DEBT_SHIFT_ID_UPDATED = 'debt_shift_id_updated',
  DEBT_PAYMENT_REVERSED = 'debt_payment_reversed',
  DEBT_REVERSED = 'debt_reversed',
}

export interface DebtChange extends Timestamps {
  id: string;
  event: DebtEvent;
  version: string;
  debt_version: string;
  updator_id: string;
  updator_name: string;
  updator_type: EntityTypes;
  changed_attributes: ChangedAttributes;
  store_id: string;
  merchant_id: string;
}

export interface GetDebtChanges {
  by_event: boolean; // get all changes for a specific event: use GSI2PK, GSI2SK
  by_event_and_debt: boolean; // get all changes for a specific event and debt: use GSI2PK, GSI2SK
  by_event_and_date_range: boolean; // get all changes for a specific event within a date range: use GSI2PK, GSI2SK
  by_debt: boolean; // get all changes for a debt: use PK, SK
  by_debt_and_date_range: boolean; // get all changes for a debt within a date range: use EntityIndexPK, EntityIndexSK
  by_updator: boolean; // get all changes made by a user: use GSI1PK, GSI1SK
  by_updator_and_date_range: boolean; // get all changes made by a user within a date range: use GSI1PK, GSI1SK
  by_updator_and_debt: boolean; // get all changes made by a user for a debt: use GSI1PK, GSI1SK

  event?: DebtEvent;
  debt_id?: string;
  updator_id?: string;
  updator_type?: EntityTypes;
  start_date?: string;
  end_date?: string;
  store_id?: string;
  merchant_id: string;
  limit?: number;
  cursor?: string;
}

export interface DebtPayment extends Timestamps, Versioned {
  id: string;
  debt_id: string;
  amount: number;
  outstanding_balance: number;
  initial_amount: number;
  payment_type: PaymentMethodType;
  user_id: string;
  payer_name: string;
  payer_id: string;
  payer_type: EntityTypes;
  store_id: string;
  merchant_id: string;
  note: string;
  origin_type?: DebtOriginType;
  plan_id?: string; // ID of the payment plan if applicable
  shift_id?: string;
  payment_type_id?: string;
  payment_type_name?: string;
  is_reversal?: boolean;
  reverses_payment_id?: string;
  reversed_by_payment_id?: string;
}

export interface PaymentPlan extends Timestamps {
  amount: number;
  frequency: number; // in days
  store_id: string;
  merchant_id: string;
  note: string;
  id: string;
}

export interface DebtChangeEvent {
  changes: ChangedAttributes;
  event: DebtEvent;
}

export interface CreateDebtCommandPayload {
  creator_id: string;
  creator_type: EntityTypes; // e.g., 'EMPLOYEE' or 'RECEIPT' if using snake case for enums, adjust accordingly
  creator_name: string;

  debtor_id: string;
  debtor_type: EntityTypes;
  debtor_name: string;

  shift_id?: string; // TODO: Make Optional

  store_id: string;
  merchant_id: string;

  origin_type: DebtOriginType; // e.g., 'sale' | 'loan' | 'transfer'
  principal_amount: number;
  interest_rate: number; // e.g., 5.5 for 5.5%
  reason: string;

  payment_type_id?: string;
  is_due_date_auto_calculated?: boolean;
  initial_outstanding_balance?: number;
  next_due_date?: string; // ISO 8601, optional if auto-calculated
  receipt_id?: string;
  payment_plan?: {
    amount: number;
    frequency: number; // in days
    note?: string;
  };
}

export interface UpdateDebtStatusCommandPayload {
  debt_id: string;
  store_id: string;
  merchant_id: string;

  updator_id: string;
  updator_type: EntityTypes;
  updator_name: string;

  status: DebtStatus; // e.g., 'ACTIVE', 'OVERDUE', etc.
}

export interface CancelDebtCommandPayload {
  debt_id: string;
  store_id: string;
  merchant_id: string;

  updator_id: string;
  updator_type: EntityTypes;
  updator_name: string;

  reason: string;
}

export interface TransferDebtCommandPayload {
  from_debt_id: string;
  new_debtor_id: string;
  new_debtor_type: EntityTypes;
  new_debtor_name: string;
  amount: number;
  note: string;
  shift_id?: string; // TODO: Make Optional

  store_id: string;
  merchant_id: string;

  creator_id: string;
  creator_type: EntityTypes;
  creator_name: string;
}

export interface UpdateDateInterestRateCommandPayload {
  debt_id: string;
  store_id: string;
  merchant_id: string;
  updator_id: string;
  updator_type: EntityTypes;
  updator_name: string;
  interest_rate: number;
}

export interface UpdateDebtPrincipalCommandPayload {
  debt_id: string;
  store_id: string;
  merchant_id: string;

  updator_id: string;
  updator_type: EntityTypes;
  updator_name: string;

  principal_amount: number;
}

//
// Payment Commands Payloads
//
export interface AddDebtPaymentCommandPayload {
  debt_id: string;
  amount: number;
  payment_type: PaymentMethodType; // e.g., 'CASH', 'CARD'
  payment_type_id?: string; // ID of the payment type if selected from PaymentType entity
  payment_type_name?: string; // Name of the payment type if selected from PaymentType entity

  payer_id: string;
  payer_type: EntityTypes;
  payer_name: string;

  store_id: string;
  merchant_id: string;
  user_id: string; // who is recording the payment
  note?: string;
  shift_id?: string;
}

export interface UpdateDebtPaymentPlanCommandPayload {
  debt_id: string;
  amount?: number;
  frequency?: number;
  note?: string;
  store_id: string;
  merchant_id: string;
  updator_id: string;
  updator_type: EntityTypes;
  updator_name: string;
}

//
// Reversal Command Payloads
//
export interface ReverseDebtPaymentCommandPayload {
  debt_id: string;
  payment_id: string;
  store_id: string;
  merchant_id: string;
  user_id: string;
  updator_type: EntityTypes;
  updator_name: string;
  reason: string;
  shift_id?: string;
}

export interface ReverseDebtCommandPayload {
  debt_id: string;
  store_id: string;
  merchant_id: string;
  user_id: string;
  updator_type: EntityTypes;
  updator_name: string;
  reason: string;
  shift_id?: string;
  payment_type_id: string;
}

export enum DebtCommandType {
  CREATE_DEBT = 'CREATE_DEBT',
  UPDATE_DEBT_STATUS = 'UPDATE_DEBT_STATUS',
  CANCEL_DEBT = 'CANCEL_DEBT',
  TRANSFER_DEBT = 'TRANSFER_DEBT',
  UPDATE_INTEREST_RATE = 'UPDATE_INTEREST_RATE',
  UPDATE_PRINCIPAL_AMOUNT = 'UPDATE_PRINCIPAL_AMOUNT',
  ADD_PAYMENT = 'ADD_PAYMENT',
  UPDATE_PAYMENT_PLAN = 'UPDATE_PAYMENT_PLAN',
  REVERSE_PAYMENT = 'REVERSE_PAYMENT',
  REVERSE_DEBT = 'REVERSE_DEBT',
}

export interface DebtCommand {
  command: DebtCommandType;
  createDebtCommandPayload?: CreateDebtCommandPayload;
  updateDebtStatusCommandPayload?: UpdateDebtStatusCommandPayload;
  cancelDebtCommandPayload?: CancelDebtCommandPayload;
  transferDebtCommandPayload?: TransferDebtCommandPayload;
  updateDateInterestRateCommandPayload?: UpdateDateInterestRateCommandPayload;
  updateDebtPrincipalCommandPayload?: UpdateDebtPrincipalCommandPayload;
  addDebtPaymentCommandPayload?: AddDebtPaymentCommandPayload;
  updateDebtPaymentPlanCommandPayload?: UpdateDebtPaymentPlanCommandPayload;
  reverseDebtPaymentCommandPayload?: ReverseDebtPaymentCommandPayload;
  reverseDebtCommandPayload?: ReverseDebtCommandPayload;
}

export interface DebtDBItem extends Debt {
  PK: string; // MERCHANT#<merchant_id>#STORE#<store_id>
  SK: string; // DEBT#<debt_id>
  EntityIndexPK: string; // DEBT
  EntityIndexSK: string; // MERCHANT#<merchant_id>#STORE#<store_id>#DATE#<created_at>
  GSI1PK: string; // STORE#<store_id>#DEBT_STATUS#<debt_status>
  GSI1SK: string; // DEBT#DATE#<created_at>
  GSI2PK: string; // STORE#<store_id>#DEBT_ORGIN#<debt_orign>
  GSI2SK: string; // DEBT#DATE#<created_at>
  GSI3PK: string; // <debtor_type>#<debtor_id>
  GSI3SK: string; // DEBT#MERCHANT#<merchant_id>#STORE#<store_id>#DATE#<created_at>
  GSI4PK: string; // <creator_type>#<creator_id>
  GSI4SK: string; // DEBT#MERCHANT#<merchant_id>#STORE#<store_id>#DATE#<created_at>
  GSI5PK?: string; // MERCHANT#<merchant_id>#STORE#<store_id>#RECEIPT#<receipt_id>
  GSI5SK?: string; // DEBT#<debt_id>#DATE#<created_at>
}

export interface DebtChangeDBItem extends DebtChange {
  PK: string; // DEBT#<debt_id>
  SK: string; // DEBT_CHANGE#<version>
  EntityIndexPK: string; // DEBT_CHANGE
  EntityIndexSK: string; // MERCHANT#<merchant_id>#STORE#<store_id>#DATE#<created_at>
  GSI1PK: string; // <updator_type>#<updator_id>
  GSI1SK: string; // DEBT_CHANGE#DEBT#<debt_id>#DATE#<created_at>
  GSI2PK: string; // STORE#<store_id>#DEBT_CHANGE#<event>
  GSI2SK: string; // DEBT#<debt_id>#DATE#<created_at>
}

export interface DebtPaymentDBItem extends DebtPayment {
  PK: string; // DEBT#<debt_id>
  SK: string; // DEBT_PAYMENT#<payment_id>
  EntityIndexPK: string; // DEBT_PAYMENT
  EntityIndexSK: string; // DEBT_PAYMENT#MERCHANT#<merchant_id>#STORE#<store_id>#DATE#<created_at>
  GSI1PK: string; // <payer_type>#<payer_id>
  GSI1SK: string; // DEBT_PAYMENT#DATE#<created_at>
  GSI2PK: string; // STORE#<store_id>
  GSI2SK: string; // DEBT_PAYMENT#DATE#<created_at>
  GSI3PK: string; // USER#<user_id>
  GSI3SK: string; // DEBT_PAYMENT#DATE#<created_at>
  GSI4PK: string; // DEBT#<debt_id>
  GSI4SK: string; // DEBT_PAYMENT#DATE#<created_at>
}

export interface GetDebtAggregate {
  debt_id: string;
  merchant_id: string;
  store_id: string;
  change_cursor?: string;
  payment_cursor?: string;
  change_limit?: number;
  payment_limit?: number;
}

export interface GetDebts {
  by_store?: boolean; // get all debts for a store: use PK, SK
  by_store_and_date_range?: boolean; // get all debts for a store within a date range: use EntityIndexPK, EntityIndexSK
  by_store_and_status?: boolean; // get all debts for a store with a specific status: use GSI1PK, GSI1SK
  by_store_and_status_and_date_range?: boolean; // get all debts for a store with a specific status within a date range: use GSI1PK, GSI1SK
  by_store_and_origin?: boolean; // get all debts for a store with a specific origin: use GSI2PK, GSI2SK
  by_store_and_origin_and_date_range?: boolean; // get all debts for a store with a specific origin within a date range: use GSI2PK, GSI2SK
  by_creator?: boolean; // get all debts created by a user: use GSI4PK, GSI4SK
  by_creator_and_date_range?: boolean; // get all debts created by a user within a date range: use GSI4PK, GSI4SK
  by_debtor?: boolean; // get all debts for a debtor: use GSI3PK, GSI3SK
  by_debtor_and_store?: boolean; // get all debts for a debtor for a store: use GSI3PK, GSI3SK

  by_debtor_and_date_range?: boolean; // get all debts for a debtor within a date range: use GSI3PK, GSI3SK
  by_merchant?: boolean; // get all debts for a merchant: use EntityIndexPK, EntityIndexSK
  by_merchant_and_date_range?: boolean; // get all debts for a merchant within a date range: use EntityIndexPK, EntityIndexSK
  by_receipt?: boolean; // get all debts for a receipt: use GSI5PK, GSI5SK
  store_id?: string;
  merchant_id: string;
  status?: DebtStatus;
  origin?: DebtOriginType;
  debtor_id?: string;
  debtor_type?: EntityTypes;
  creator_id?: string;
  creator_type?: EntityTypes;
  start_date?: string;
  end_date?: string;
  receipt_id?: string;
  limit?: number;
  cursor?: string;
  with_debts?: boolean;
}

export interface GetDebtPayments {
  by_debt?: boolean; // get all payments for a debt: use PK, SK
  by_debt_and_date_range?: boolean; // get all payments for a debt within a date range: use GSI4PK, GSI4SK
  by_payer?: boolean; // get all payments made by a payer: use GSI1PK, GSI1SK
  by_payer_and_date_range?: boolean; // get all payments made by a payer within a date range: use GSI1PK, GSI1SK
  by_merchant?: boolean; // get all payments for a merchant: use EntityIndexPK, EntityIndexSK
  by_merchant_and_date_range?: boolean; // get all payments for a merchant within a date range: use EntityIndexPK, EntityIndexSK
  by_store?: boolean; // get all payments for a store: use GSI2PK, GSI2SK
  by_store_and_date_range?: boolean; // get all payments for a store within a date range: use GSI2PK, GSI2SK
  debt_id?: string;
  payer_id?: string;
  payer_type?: EntityTypes;
  store_id?: string;
  merchant_id: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
  cursor?: string;
}

export interface DebtPaymentsPage {
  payments: DebtPayment[];
  cursor: string;
  count: number;
}

export interface DebtChangesPage {
  changes: DebtChange[];
  cursor: string;
  count: number;
}

export interface DebtsPage {
  debts: Debt[];
  cursor: string;
  count: number;
}

export interface DebtAggregate {
  debt: Debt;
  changes: DebtChangesPage;
  payments: DebtPaymentsPage;
}

export interface DebtorSummary {
  debtor_id: string;
  debtor_type: EntityTypes;
  debtor_name: string;
  total_money_debt: number;
  total_money_payments: number;
  total_money_outstanding: number;
  total_money_overdue: number;
  total_money_defaulted: number;
  total_money_closed: number;
  total_money_interest: number;
  total_money_principal: number;
  total_money_transferred_out: number;
  total_money_transferred_in: number;
  next_due_date: string;
  stores: string[];
  overdue_count: number;
  debt_count: number;
  closed_count: number;
  debts: DebtAggregate[];
  /** Cursor for paginating closed/terminal debts. Undefined when there are no more pages. */
  closed_debt_cursor?: string;
  /** Whether there are more closed debts beyond the current page. */
  has_more_closed_debts?: boolean;
}

// =============================================================================
// Outstanding Balance (aggregate endpoint)
// =============================================================================

export interface OutstandingBalanceResponse {
  outstanding_balance: number;
  debt_count: number;
}

export interface GetOutstandingBalanceDto {
  merchant_id: string;
  store_id: string;
  as_of: string;
}

export interface GetDebtorSummary {
  debtor_id: string;
  debtor_type: EntityTypes;
  merchant_id: string;
  store_id: string;
  with_debts?: boolean;
  /** Cursor for paginating closed/terminal debts. Omit for the first page. */
  closed_debt_cursor?: string;
  /** Number of closed debts per page (default 10). */
  closed_debt_limit?: number;
}
